import { Alert } from "@spz/ui/components/alert";
import { Checkbox } from "@spz/ui/components/checkbox";
import { Icon, type IconSymbol } from "@spz/ui/components/icon";
// DataTable.tsx
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table as UITable,
} from "@spz/ui/components/table";
import { Typography } from "@spz/ui/components/typography";
import {
  type CellContext,
  type Column,
  type ColumnDef,
  type SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn, interval } from "../../lib/utils";
import { Hint } from "../hint";
import { Skeleton } from "../skeleton";
import { CellRenderer } from "./cell-renderer";
import { ButtonCell } from "./cells";
import SortArrows from "./sort-arrows";
import type { DataTableColumn, DataTableProps } from "./types";
import { getPinningStyles } from "./utils/table-utils";

// Number of skeleton rows to display during loading
const SKELETON_ROW_COUNT = 8;
// Number of skeleton columns to display during loading
const SKELETON_COLUMN_COUNT = 6;

export function DataTable<T extends { id: unknown }>({
  columns,
  data,
  enableSelection = false,
  actions,
  className,
  noDataMessage,
  onRowClick,
  pinnedColumns,
  isLoading = false,
  columnsLoading = false,
  highlightedRowId,
  selectedRows = [],
  setSelectedRows = () => {},
  onSortChange,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columnHelper = createColumnHelper<T>();
  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding onSortChange as a dependency can cause no sort change
  useEffect(() => {
    onSortChange?.({
      sortField: sorting[0]?.id ?? undefined,
      sortDirection:
        sorting.length > 0 ? (sorting[0]?.desc ? "DESC" : "ASC") : undefined,
    });
  }, [sorting]);

  const handleSortIconClick = useCallback((column: Column<T, unknown>) => {
    const current = column.getIsSorted();
    if (!current) {
      column.toggleSorting(false);
    } else if (current === "asc") {
      column.toggleSorting(true);
    } else {
      setSorting([]);
    }
  }, []);

  // Create selection column if enabled
  const selectionColumn = useMemo(
    () =>
      enableSelection
        ? columnHelper.display({
            id: "select",
            header: ({ table }) => {
              const isAllPageRowsSelected = table.getIsAllPageRowsSelected();
              const isSomeRowsSelected = table.getIsSomeRowsSelected();

              return (
                <Checkbox
                  checked={
                    isAllPageRowsSelected
                      ? true
                      : isSomeRowsSelected
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(checked) => {
                    table.toggleAllPageRowsSelected(!!checked);
                  }}
                  aria-label="Select all"
                />
              );
            },
            cell: ({ row }) => (
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(checked) => {
                  row.toggleSelected(!!checked);
                }}
                aria-label="Select row"
                onClick={(e) => e.stopPropagation()}
              />
            ),
            enableSorting: false,
            maxSize: 48,
            size: 48,
          })
        : null,
    [enableSelection, columnHelper.display],
  );

  // Create actions column if actions are provided
  const actionsColumn = useMemo(
    () =>
      actions
        ? columnHelper.display({
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
              <div className="flex gap-2 justify-end">
                {actions
                  .filter((action) => !action.hidden?.(row.original))
                  .map((action, index) => (
                    <ButtonCell
                      key={row.id}
                      buttons={[
                        {
                          label: action.label,
                          icon: action.icon as IconSymbol,
                          onClick: () => action.onClick(row.original),
                          type: "button",
                        },
                      ]}
                    />
                  ))}
              </div>
            ),
            enableSorting: false,
          })
        : null,
    [actions, columnHelper.display],
  );

  // Combine all columns
  const tableColumns = useMemo(() => {
    const cols: ColumnDef<T, unknown>[] = [];
    // implement loading state for columns
    if (!columns || columnsLoading) {
      // Return 6 skeleton columns, adjust the count if needed
      for (let i = 0; i < SKELETON_COLUMN_COUNT; i++) {
        cols.push({
          id: `skeleton-col-${i}`,
          header: () => <Skeleton className="h-3 w-10" />,
        });
      }
      return cols;
    }
    if (selectionColumn) cols.push(selectionColumn);

    cols.push(
      ...columns.map((col) =>
        columnHelper.accessor(
          (row) => row[col.accessorKey as keyof T] as unknown,
          {
            id: col.accessorFn ? col.id : col.accessorKey.toString(),
            header: ({ column }) => (
              <div className="flex items-center gap-2">
                {col.header}
                {col.enableSorting && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSortIconClick(column);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSortIconClick(column);
                      }
                    }}
                    className="cursor-pointer p-1"
                  >
                    <SortArrows order={column.getIsSorted()} />
                  </div>
                )}
              </div>
            ),
            cell: (info: CellContext<T, unknown>) => {
              const cellValueRetriever = col.accessorFn
                ? (info: CellContext<T, unknown>) => {
                    const row = info.row.original;
                    return col?.accessorFn?.(row) ?? info.getValue();
                  }
                : (info: CellContext<T, unknown>) => {
                    return info.getValue();
                  };

              const value = cellValueRetriever(info);
              const row = info.row.original;

              const props =
                typeof col.cellProps === "function"
                  ? (
                      col.cellProps as unknown as (
                        row: T,
                      ) => Record<string, unknown>
                    )(row)
                  : col.cellProps;

              return (
                <CellRenderer
                  column={{
                    type: col.type,
                    accessorKey: col.accessorKey as keyof T & string,
                    cellProps: props,
                  }}
                  value={value}
                  row={row}
                />
              );
            },
            enableSorting: col.enableSorting,
            size: col.size,
            minSize: col.minSize,
            maxSize: col.maxSize,
          },
        ),
      ),
    );

    if (actionsColumn) cols.push(actionsColumn);
    return cols;
  }, [
    columns,
    columnsLoading,
    selectionColumn,
    actionsColumn,
    columnHelper,
    handleSortIconClick,
  ]);

  const rowSelection = useMemo(() => {
    return selectedRows.reduce(
      (acc, id) => {
        acc[id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [selectedRows]);

  const setRowSelection = useCallback(
    (rowSelection: Record<string, boolean>) => {
      setSelectedRows(Object.keys(rowSelection).filter((r) => rowSelection[r]));
    },
    [setSelectedRows],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      rowSelection,
    },
    initialState: {
      columnPinning: {
        left: pinnedColumns?.left ?? ["select"],
        right: pinnedColumns?.right ?? ["actions"],
      },
    },
    enableColumnPinning: true,
    enableRowSelection: enableSelection,
    getRowId: (row) => row.id as string,
    onSortingChange: setSorting,
    onRowSelectionChange: (updaterOrValue) => {
      if (typeof updaterOrValue === "function") {
        setRowSelection(updaterOrValue(rowSelection));
      } else {
        setRowSelection(updaterOrValue);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  if (!columns || columns.length === 0) {
    return <Hint variant="info" text={t("no_data")} horizontal={false} />;
  }
  function validateColumnsDataMatch(columns: DataTableColumn<T>[], data: T[]) {
    if (!Array.isArray(data) || data.length === 0) return;
    // biome-ignore lint/suspicious/noExplicitAny: <FIXME: This is a temporary solution to fix an issue>
    const row = data[0] as any;
    if (!row.data) {
      return;
    }
    for (const col of columns) {
      if (
        col.accessorKey &&
        col.accessorKey !== "actions" &&
        col.accessorKey !== "select" &&
        col.accessorKey !== "ownerId" &&
        col.accessorKey !== "id" &&
        col.accessorKey !== "status" &&
        col.accessorKey !== "conclusion" &&
        !(col.accessorKey in row.data)
      ) {
        throw new Error("Some columns do not match the data provided.");
      }
    }
  }
  validateColumnsDataMatch(columns, data);
  return (
    <div className={cn("w-full relative", className)}>
      <div className="overflow-hidden">
        <UITable className="overflow-auto w-full border-collapse border-spacing-0 bg-white">
          <TableHeader className={cn("relative")}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      allSelected={table.getIsAllPageRowsSelected()}
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        "relative",
                        header.column.getCanSort() && "select-none",
                      )}
                      style={getPinningStyles(header.column, true)}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              interval(SKELETON_ROW_COUNT).map((rowNum) => (
                <TableRow key={`skeleton-row-${rowNum}`}>
                  {tableColumns.map((column) => (
                    <TableCell key={`skeleton-cell-${rowNum}-${column.id}`}>
                      <Skeleton className="h-3" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={tableColumns.length}
                  className="text-center"
                >
                  <div className="min-h-[150px] flex items-center justify-center flex-col gap-2">
                    <Alert withIcon>
                      <Icon icon="info" size={18} className="text-blue-500" />
                    </Alert>{" "}
                    <Typography
                      variant="body"
                      className="text-center font-semibold"
                    >
                      {noDataMessage ?? t("no_results")}
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isSelected = row.getIsSelected();
                const isHighlighted = highlightedRowId === row.original.id;

                return (
                  <TableRow
                    key={row.id}
                    onClick={() => onRowClick?.(row.original)}
                    className={cn("group/row", "transition-colors")}
                    data-state={isSelected ? "selected" : undefined}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isPinned = cell.column.getIsPinned();

                      return (
                        <TableCell
                          key={cell.id}
                          className={cn("relative transition-colors", {
                            "bg-blue-50": isHighlighted || row.getIsSelected(),
                            "bg-white": isPinned && !isHighlighted,
                            "group-hover/row:bg-blue-50":
                              !isHighlighted && !isSelected,
                            "!bg-blue-50 group-hover/row:!bg-blue-50":
                              isSelected && !isHighlighted,
                          })}
                          style={getPinningStyles(cell.column)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </UITable>
      </div>
    </div>
  );
}
